import React, { useEffect, useState, useCallback, useMemo } from "react";
import { AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, ReferenceLine, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { api, formatBg } from "../lib/glucose";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { ChartLine, ChartBar, Rows, PresentationChart, Printer, DownloadSimple, CalendarBlank } from "@phosphor-icons/react";

const RANGES = [
  { value: 7, label: "7d" },
  { value: 14, label: "14d" },
  { value: 30, label: "30d" },
  { value: 60, label: "60d" },
  { value: 90, label: "90d" },
];

export default function Reports() {
  const { user } = useAuth();
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const units = user?.units || "mg/dL";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/reports", { params: { days, tz_offset_minutes: -new Date().getTimezoneOffset() } });
      setData(r.data);
    } catch {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const RED = "hsl(var(--sig-urgent))";
  const YELLOW = "hsl(var(--sig-high))";
  const GREEN = "hsl(var(--sig-in-range))";
  const INK = "hsl(var(--foreground))";

  return (
    <div className="space-y-8" data-testid="reports-root">
      {/* Print-only header — visible only on paper / PDF */}
      <div data-print-only className="mb-4 pb-3 border-b border-black">
        <div className="flex items-baseline justify-between">
          <div>
            <div style={{ fontSize: "18pt", fontWeight: 600 }}>GlucoCloud Report</div>
            {user && <div style={{ fontSize: "10pt" }}>Patient: {user.name} · {user.email}</div>}
          </div>
          <div style={{ fontSize: "9pt", textAlign: "right" }}>
            Generated {new Date().toLocaleString()}<br />
            Window: last {days} days · Target {formatBg(data?.target_low ?? 80, units)}–{formatBg(data?.target_high ?? 160, units)} {units}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Analytics</div>
          <h1 className="font-display text-2xl sm:text-3xl tracking-tight">Reports</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Ambulatory Glucose Profile, distribution, and day-by-day breakdown across a rolling window.
          </p>
        </div>
        <div className="flex items-center gap-2" data-print-hide>
          <ToggleGroup
            type="single"
            value={String(days)}
            onValueChange={(v) => v && setDays(Number(v))}
            className="border border-border rounded-md"
            data-testid="reports-range-toggle"
          >
            {RANGES.map((r) => (
              <ToggleGroupItem key={r.value} value={String(r.value)} data-testid={`reports-range-${r.label}`} className="px-3 h-9 text-xs">
                {r.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <Button variant="outline" onClick={() => window.print()} data-testid="reports-print-btn" className="gap-1.5 h-9">
            <Printer size={14} /> Print / PDF
          </Button>
        </div>
      </div>

      {loading && <div className="text-sm text-muted-foreground" data-testid="reports-loading">Loading…</div>}
      {!loading && data?.count === 0 && (
        <div className="p-10 rounded-lg border border-border text-center text-sm text-muted-foreground" data-testid="reports-empty">
          No readings in the last {days} days.
        </div>
      )}

      {data && data.count > 0 && (
        <>
          {/* Overall stats */}
          <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4" data-testid="reports-overall">
            <ReportStat label="Readings" value={data.overall.count.toLocaleString()} sub={`over ${data.days}d`} />
            <ReportStat label="Time in Range" value={`${data.overall.tir}%`} color="sig-in-range" sub={`${formatBg(data.target_low, units)}–${formatBg(data.target_high, units)}`} />
            <ReportStat label="Average" value={formatBg(data.overall.avg, units)} sub={units} />
            <ReportStat label="GMI" value={`${data.overall.gmi}%`} sub="Glucose Mgmt. Index" />
            <ReportStat label="Est. HbA1c" value={`${data.overall.hba1c}%`} sub="eA1c formula" />
            <ReportStat label="CV" value={`${data.overall.cv}%`} sub="Variability" />
          </section>

          {/* AGP */}
          <section className="rounded-lg border border-border bg-card p-4 sm:p-6" data-testid="reports-agp">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <PresentationChart size={18} weight="duotone" />
                <div className="font-display font-medium text-lg">Ambulatory Glucose Profile</div>
              </div>
              <div className="text-xs text-muted-foreground">Hour-of-day percentiles · median line, IQR &amp; 5–95 bands</div>
            </div>
            <ResponsiveContainer width="100%" height={340}>
              <AreaChart data={data.agp} margin={{ top: 8, right: 20, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="p5-95" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={INK} stopOpacity={0.06} /><stop offset="100%" stopColor={INK} stopOpacity={0.06} /></linearGradient>
                  <linearGradient id="iqr" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={INK} stopOpacity={0.18} /><stop offset="100%" stopColor={INK} stopOpacity={0.18} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="hour" type="number" domain={[0, 23]} ticks={[0, 3, 6, 9, 12, 15, 18, 21]} tickFormatter={(h) => `${h}:00`} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis domain={[40, 400]} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={40} tickFormatter={(v) => units === "mmol/L" ? (v * 0.0555).toFixed(1) : v} />
                <ReferenceLine y={data.alert_high} stroke={YELLOW} strokeDasharray="4 4" strokeOpacity={0.55} />
                <ReferenceLine y={data.alert_low} stroke={RED} strokeDasharray="4 4" strokeOpacity={0.55} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                  labelFormatter={(h) => `${String(h).padStart(2, "0")}:00`}
                  formatter={(v, name) => v == null ? null : [`${formatBg(v, units)} ${units}`, name]}
                />
                {/* 5-95 band */}
                <Area type="monotone" dataKey="p95" stroke="none" fill="url(#p5-95)" isAnimationActive={false} name="p95" />
                <Area type="monotone" dataKey="p5" stroke="none" fill="hsl(var(--background))" isAnimationActive={false} name="p5" />
                {/* IQR band */}
                <Area type="monotone" dataKey="p75" stroke="none" fill="url(#iqr)" isAnimationActive={false} name="p75" />
                <Area type="monotone" dataKey="p25" stroke="none" fill="hsl(var(--background))" isAnimationActive={false} name="p25" />
                {/* median */}
                <Line type="monotone" dataKey="p50" stroke={INK} strokeWidth={2.25} dot={false} isAnimationActive={false} name="median" />
              </AreaChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-6 text-xs text-muted-foreground mt-3">
              <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-foreground" /> Median (p50)</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-foreground/20" /> IQR (25–75)</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-foreground/10" /> 5–95 percentile</span>
            </div>
          </section>

          {/* Distribution */}
          <section className="rounded-lg border border-border bg-card p-4 sm:p-6" data-testid="reports-distribution">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <ChartBar size={18} weight="duotone" />
                <div className="font-display font-medium text-lg">Distribution</div>
              </div>
              <div className="text-xs text-muted-foreground">10 mg/dL bins</div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.distribution} margin={{ top: 8, right: 20, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="lo" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => units === "mmol/L" ? (v * 0.0555).toFixed(1) : v} interval={3} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={36} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                  labelFormatter={(v, payload) => {
                    if (!payload?.[0]) return "";
                    const b = payload[0].payload;
                    return `${formatBg(b.lo, units)}–${formatBg(b.hi, units)} ${units}`;
                  }}
                  formatter={(v) => [`${v} readings`, "count"]}
                />
                <Bar dataKey="count" isAnimationActive={false} shape={(props) => {
                  const b = props.payload;
                  const mid = (b.lo + b.hi) / 2;
                  const fill = mid < data.alert_low ? RED : mid > data.alert_high ? YELLOW : GREEN;
                  return <rect x={props.x} y={props.y} width={props.width} height={props.height} fill={fill} />;
                }} />
              </BarChart>
            </ResponsiveContainer>
          </section>

          {/* Daily stats table */}
          <section className="rounded-lg border border-border bg-card p-4 sm:p-6" data-testid="reports-daily">
            <div className="flex items-center gap-2 mb-4">
              <Rows size={18} weight="duotone" />
              <div className="font-display font-medium text-lg">Daily breakdown</div>
            </div>
            <div className="overflow-x-auto">
              <Table className="min-w-[700px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Readings</TableHead>
                    <TableHead className="text-right">Avg</TableHead>
                    <TableHead className="text-right">Min</TableHead>
                    <TableHead className="text-right">Max</TableHead>
                    <TableHead className="text-right">TIR</TableHead>
                    <TableHead className="text-right">Low</TableHead>
                    <TableHead className="text-right">High</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...data.daily].reverse().map((d) => (
                    <TableRow key={d.day} data-testid={`daily-row-${d.day}`}>
                      <TableCell className="tabular font-medium">{d.day}</TableCell>
                      <TableCell className="text-right tabular">{d.count}</TableCell>
                      <TableCell className="text-right tabular">{formatBg(d.avg, units)}</TableCell>
                      <TableCell className="text-right tabular sig-urgent">{formatBg(d.min, units)}</TableCell>
                      <TableCell className="text-right tabular sig-high">{formatBg(d.max, units)}</TableCell>
                      <TableCell className="text-right tabular sig-in-range">{d.tir}%</TableCell>
                      <TableCell className="text-right tabular sig-urgent">{d.low_pct}%</TableCell>
                      <TableCell className="text-right tabular sig-high">{d.high_pct}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function ReportStat({ label, value, sub, color }) {
  return (
    <div className="p-4 rounded-md border border-border bg-card">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`font-display text-2xl tabular mt-2 ${color || ""}`}>{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>
    </div>
  );
}
